import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { FuseNavigationItem, FuseNavigationService } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { defaultNavigation } from 'app/mock-api/common/navigation/data';
import { contacts } from 'app/mock-api/apps/contacts/data';
import { tasks } from 'app/mock-api/apps/tasks/data';
import { SessionStorageService } from 'app/core/services/session-storage.service';

@Injectable({
    providedIn: 'root'
})
export class SearchMockApi
{
    private _defaultNavigation: FuseNavigationItem[];
    private readonly _contacts: any[] = contacts;
    private readonly _tasks: any[] = tasks;

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _fuseNavigationService: FuseNavigationService,
        private sessionStorageService: SessionStorageService
    )
    {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Filter navigation items by allowedRoles and user role
     */
    filterNavigationByRole(navigation: FuseNavigationItem[], roles: string[]): FuseNavigationItem[] {
        return navigation
            .filter(item => {
                // If allowedRoles is not defined, show the item
                if (!item.allowedRoles) return true;
                // If allowedRoles is defined, check if any of the roles are included
                return roles.some(role => item.allowedRoles.includes(role));
            })
            .map(item => {
                // If the item has children, filter them recursively
                if (item.children) {
                    const filteredChildren = this.filterNavigationByRole(item.children, roles);
                    // Only include the item if it has visible children
                    if (filteredChildren.length > 0) {
                        return { ...item, children: filteredChildren };
                    } else {
                        // If no children are visible, exclude the parent
                        return null;
                    }
                }
                return { ...item };
            })
            .filter(item => item !== null);
    }
    /**
     * Register Mock API handlers
     */
    registerHandlers(): void
    {
        const sessionUser = this.sessionStorageService.getUser();
        const roles = sessionUser?.roles || [];
        
        if (roles && roles.length > 0) {
           this._defaultNavigation = this.filterNavigationByRole(
               defaultNavigation,
               roles.map((role: any) => (typeof role === 'string' ? role : role.level).trim())
           );
        } else {
            this._defaultNavigation = defaultNavigation;
        }
        // Get the flat navigation and store it
        const flatNavigation = this._fuseNavigationService.getFlatNavigation(this._defaultNavigation);

        // -----------------------------------------------------------------------------------------------------
        // @ Search results - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/common/search')
            .reply(({request}) => {

                // Get the search query
                const query = cloneDeep(request.body.query.toLowerCase());

                // If the search query is an empty string,
                // return an empty array
                if ( query === '' )
                {
                    return [200, {results: []}];
                }

                // Filter the contacts
                const contactsResults = cloneDeep(this._contacts)
                    .filter(contact => contact.name.toLowerCase().includes(query));

                // Filter the navigation
                const pagesResults = cloneDeep(flatNavigation)
                    .filter(page => (page.title?.toLowerCase().includes(query) || (page.subtitle && page.subtitle.includes(query))));

                // Filter the tasks
                const tasksResults = cloneDeep(this._tasks)
                    .filter(task => task.title.toLowerCase().includes(query));

                // Prepare the results array
                const results = [];

                // If there are contacts results...
                if ( contactsResults.length > 0 )
                {
                    // Normalize the results
                    contactsResults.forEach((result) => {

                        // Add a link
                        result.link = '/apps/contacts/' + result.id;

                        // Add the name as the value
                        result.value = result.name;
                    });

                    // Add to the results
                    results.push({
                        id     : 'contacts',
                        label  : 'Contacts',
                        results: contactsResults
                    });
                }

                // If there are page results...
                if ( pagesResults.length > 0 )
                {
                    // Normalize the results
                    pagesResults.forEach((result: any) => {

                        // Add the page title as the value
                        result.value = result.title;
                    });

                    // Add to the results
                    results.push({
                        id     : 'pages',
                        label  : 'Pages',
                        results: pagesResults
                    });
                }

                // If there are tasks results...
                if ( tasksResults.length > 0 )
                {
                    // Normalize the results
                    tasksResults.forEach((result) => {

                        // Add a link
                        result.link = '/apps/tasks/' + result.id;

                        // Add the title as the value
                        result.value = result.title;
                    });

                    // Add to the results
                    results.push({
                        id     : 'tasks',
                        label  : 'Tasks',
                        results: tasksResults
                    });
                }

                // Return the response
                return [200, results];
            });
    }
}
